import { Grid, Splash } from '@salecycle/ui-component-library';
import { TemplatesGridWrapper } from './styles';
import { useTranslation } from 'react-i18next';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { ITemplate } from 'entities/template/template';
import {
  filterByOptions,
  sortByOptions,
  useTemplatesListDispatch,
  useTemplatesListState,
} from '../context/UseTemplatesListContext';
import { TemplatePanel } from './TemplatePanel';
import { sortByAZ, sortByDateDesc, sortByDateAsc } from '../utils/sortBy';
import Spinner from 'core/ui/components/spinner';
import CreateTemplatePanel from './CreateTemplatePanel';
import { useAccessLevel, useFeatureFlags } from 'core/app/AppContext';
import { TemplateTypes } from '../constants/templateTypes';
import queryString from 'query-string';
import { isUnsubscribeTemplate } from '../../../utils/templateUtils';
import {
  useMainTemplatesQuery,
  useUnsubscribeTemplatesQuery,
  useGlobalUnsubscribeTemplatesQuery,
  useGlobalEmailTrackingUnsubscribeTemplatesQuery,
  useEmailTrackingUnsubscribeSiteTemplatesQuery,
  useEmailAcquisitionsQuery,
  useSendMyBasketQuery,
  useDisplayOnlyQuery,
  useMPSEmbedQuery,
  useMPSPopupQuery,
} from '../hooks/useTemplatesQueries';

interface IProps {
  templateType?: string;
  channel?: string;
  isArchived?: boolean;
}

const useSetDefaultSelectedTemplate = (
  templates: ITemplate[],
  unsubscribeTemplates: ITemplate[],
  globalUnsubscribeTemplates: ITemplate[],
  globalEmailTrackingUnsubscribeTemplates: ITemplate[],
  emailTrackingUnsubscribeSiteTemplates: ITemplate[],
  emailAcquisitionTemplates: ITemplate[],
  emailAcquisitionEmailTemplates: ITemplate[],
  sendMyBasketTemplates: ITemplate[],
  sendMyBasketEmailTemplates: ITemplate[],
  displayOnlyTemplates: ITemplate[],
  browseTemplates: ITemplate[],
  mpsEmbedTemplates: ITemplate[],
  mpsPopupTemplates: ITemplate[],
  smsCartAbandonmentTemplates: ITemplate[],
  smsBrowseAbandonmentTemplates: ITemplate[],
  rcsCartAbandonmentTemplates: ITemplate[],
  rcsBrowseAbandonmentTemplates: ITemplate[],
  bulkRetargetingTemplates: ITemplate[],
  /** Templates shown in this grid (same tab/type); checked before the combined list. */
  filteredGridTemplates: ITemplate[],
) => {
  const dispatch = useTemplatesListDispatch();
  const queryParams = queryString.parse(window.location.search);

  React.useEffect(() => {
    const combinedTemplates = templates
      ?.concat(unsubscribeTemplates)
      .concat(globalUnsubscribeTemplates)
      .concat(globalEmailTrackingUnsubscribeTemplates)
      .concat(emailTrackingUnsubscribeSiteTemplates)
      .concat(emailAcquisitionTemplates)
      .concat(emailAcquisitionEmailTemplates)
      .concat(sendMyBasketTemplates)
      .concat(sendMyBasketEmailTemplates)
      .concat(displayOnlyTemplates)
      .concat(browseTemplates)
      .concat(mpsEmbedTemplates)
      .concat(mpsPopupTemplates)
      .concat(smsCartAbandonmentTemplates)
      .concat(smsBrowseAbandonmentTemplates)
      .concat(rcsCartAbandonmentTemplates)
      .concat(rcsBrowseAbandonmentTemplates)
      .concat(bulkRetargetingTemplates);

    const selectedTemplateId = queryParams.selectedTemplateId?.toString();
    if (!selectedTemplateId) {
      return;
    }

    const matchedTemplate =
      filteredGridTemplates.find(template => template.id === selectedTemplateId) ??
      combinedTemplates.find(template => template.id === selectedTemplateId);
    if (!matchedTemplate) {
      return;
    }

    dispatch({
      type: 'SET_SELECTED_TEMPLATE',
      payload: {
        template: matchedTemplate,
      },
    });
  }, [
    browseTemplates,
    dispatch,
    displayOnlyTemplates,
    emailAcquisitionTemplates,
    emailAcquisitionEmailTemplates,
    globalUnsubscribeTemplates,
    globalEmailTrackingUnsubscribeTemplates,
    emailTrackingUnsubscribeSiteTemplates,
    filteredGridTemplates,
    mpsEmbedTemplates,
    mpsPopupTemplates,
    queryParams.selectedTemplateId,
    sendMyBasketEmailTemplates,
    sendMyBasketTemplates,
    smsCartAbandonmentTemplates,
    smsBrowseAbandonmentTemplates,
    rcsCartAbandonmentTemplates,
    rcsBrowseAbandonmentTemplates,
    templates,
    unsubscribeTemplates,
    bulkRetargetingTemplates,
  ]);
};

export const TemplatesGrid: React.FC<IProps> = ({ templateType, channel, isArchived }) => {
  const { t } = useTranslation('designs');
  const { accessLevel } = useAccessLevel();
  const { flagEnabled } = useFeatureFlags();
  const emailTrackingFeatureEnabled = flagEnabled('ld-email-tracking-consent');
  const isGlobalEmailTrackingUnsubscribeGrid = templateType === TemplateTypes.globalUnsubscribeEmailTracking;
  const isSiteEmailTrackingUnsubscribeGrid = templateType === TemplateTypes.unsubscribeEmailTracking;
  const { siteId } = useParams<{ siteId: string }>();
  const { filterBy, sortingFilter, supportedLanguages, selectedTemplate } = useTemplatesListState();

  const templatesQuery = useMainTemplatesQuery(siteId, { isArchived, channel, templateType });
  const unsubscribesQuery = useUnsubscribeTemplatesQuery(siteId);
  const emailAcquisitionsQuery = useEmailAcquisitionsQuery(siteId);
  const sendMyBasketQuery = useSendMyBasketQuery(siteId);
  const displayOnlyQuery = useDisplayOnlyQuery(siteId);
  const globalUnsubscribesQuery = useGlobalUnsubscribeTemplatesQuery(siteId, accessLevel);
  const globalEmailTrackingUnsubscribesQuery = useGlobalEmailTrackingUnsubscribeTemplatesQuery(siteId, accessLevel, {
    enabled: emailTrackingFeatureEnabled && isGlobalEmailTrackingUnsubscribeGrid,
  });
  const emailTrackingUnsubscribeSiteQuery = useEmailTrackingUnsubscribeSiteTemplatesQuery(siteId, {
    enabled: emailTrackingFeatureEnabled && isSiteEmailTrackingUnsubscribeGrid,
  });
  const mpsEmbedQuery = useMPSEmbedQuery(siteId);
  const mpsPopupQuery = useMPSPopupQuery(siteId);

  const templatesQueryData = templatesQuery.data;
  const isArchivedData = isArchived && Array.isArray(templatesQueryData);

  const {
    emailCartAbandonment: templates = [],
    emailBrowseAbandonment: browseTemplates = [],
    smsCartAbandonment: smsCartAbandonmentTemplates = [],
    smsBrowseAbandonment: smsBrowseAbandonmentTemplates = [],
    rcsCartAbandonment: rcsCartAbandonmentTemplates = [],
    rcsBrowseAbandonment: rcsBrowseAbandonmentTemplates = [],
    sendMyBasketEmail: sendMyBasketEmailTemplates = [],
    emailAcquisitionEmail: emailAcquisitionEmailTemplates = [],
    bulkRetargeting: bulkRetargetingTemplates = [],
  } = isArchivedData ? {} : templatesQueryData ?? {};
  const unsubscribeTemplates = React.useMemo(() => unsubscribesQuery.data ?? [], [unsubscribesQuery.data]);
  const globalUnsubscribeTemplates = React.useMemo(() => globalUnsubscribesQuery.data ?? [], [
    globalUnsubscribesQuery.data,
  ]);
  const globalEmailTrackingUnsubscribeTemplates = React.useMemo(() => globalEmailTrackingUnsubscribesQuery.data ?? [], [
    globalEmailTrackingUnsubscribesQuery.data,
  ]);
  const emailTrackingUnsubscribeSiteTemplates = React.useMemo(() => emailTrackingUnsubscribeSiteQuery.data ?? [], [
    emailTrackingUnsubscribeSiteQuery.data,
  ]);
  const emailAcquisitionTemplates = React.useMemo(() => emailAcquisitionsQuery.data ?? [], [
    emailAcquisitionsQuery.data,
  ]);
  const sendMyBasketTemplates = React.useMemo(() => sendMyBasketQuery.data ?? [], [sendMyBasketQuery.data]);
  const displayOnlyTemplates = React.useMemo(() => displayOnlyQuery.data ?? [], [displayOnlyQuery.data]);
  const mpsEmbedTemplates = React.useMemo(() => mpsEmbedQuery.data ?? [], [mpsEmbedQuery.data]);
  const mpsPopupTemplates = React.useMemo(() => mpsPopupQuery.data ?? [], [mpsPopupQuery.data]);

  const filteredTemplates = React.useMemo(() => {
    if (isArchived && isArchivedData) {
      // Unsubscribe templates cannot be archived, so they should not appear in the archive view
      let filteredTemplates: ITemplate[] = (templatesQueryData as ITemplate[]).filter(
        template => !isUnsubscribeTemplate(template),
      );

      switch (filterBy) {
        case filterByOptions.displayTypePopup:
          filteredTemplates = filteredTemplates.filter(s => !s.isEmbedded);
          break;
        case filterByOptions.displayTypeEmbedded:
          filteredTemplates = filteredTemplates.filter(s => s.isEmbedded);
          break;
      }

      return filteredTemplates;
    }

    // For non-archived templates, use the existing logic with context templates
    let filteredTemplates: ITemplate[] = [];
    switch (templateType) {
      case TemplateTypes.unsubscribe:
        filteredTemplates = unsubscribeTemplates;
        break;
      case TemplateTypes.globalUnsubscribe:
        filteredTemplates = globalUnsubscribeTemplates;
        break;
      case TemplateTypes.globalUnsubscribeEmailTracking:
        filteredTemplates = globalEmailTrackingUnsubscribeTemplates;
        break;
      case TemplateTypes.unsubscribeEmailTracking:
        filteredTemplates = emailTrackingUnsubscribeSiteTemplates;
        break;
      case TemplateTypes.dataAcquisition:
        if (channel === 'onsite') {
          filteredTemplates = emailAcquisitionTemplates;
        } else {
          filteredTemplates = emailAcquisitionEmailTemplates;
        }
        break;
      case TemplateTypes.sendMyBasket:
        if (channel === 'onsite') {
          filteredTemplates = sendMyBasketTemplates;
        } else {
          filteredTemplates = sendMyBasketEmailTemplates;
        }
        break;
      case TemplateTypes.displayOnly:
        filteredTemplates = displayOnlyTemplates;
        break;
      case TemplateTypes.browseAbandonment:
        filteredTemplates = browseTemplates;
        break;
      case TemplateTypes.mpsEmbed:
        filteredTemplates = mpsEmbedTemplates;
        break;
      case TemplateTypes.mpsPopup:
        filteredTemplates = mpsPopupTemplates;
        break;
      case TemplateTypes.bulkRetargeting:
        filteredTemplates = bulkRetargetingTemplates;
        break;
      default:
        filteredTemplates = templates;
        break;
    }

    if (channel === 'sms' || channel === 'rcs') {
      filteredTemplates =
        templateType === TemplateTypes.browseAbandonment
          ? channel === 'sms'
            ? smsBrowseAbandonmentTemplates
            : rcsBrowseAbandonmentTemplates
          : channel === 'sms'
          ? smsCartAbandonmentTemplates
          : rcsCartAbandonmentTemplates;
    }

    switch (filterBy) {
      case filterByOptions.displayTypePopup:
        filteredTemplates = filteredTemplates.filter(s => !s.isEmbedded);
        break;
      case filterByOptions.displayTypeEmbedded:
        filteredTemplates = filteredTemplates.filter(s => s.isEmbedded);
        break;
    }

    if (!isArchived) {
      filteredTemplates = filteredTemplates.filter(t => t.isArchived !== true);
    }

    return filteredTemplates;
  }, [
    templateType,
    channel,
    displayOnlyTemplates,
    emailAcquisitionTemplates,
    emailAcquisitionEmailTemplates,
    filterBy,
    unsubscribeTemplates,
    globalUnsubscribeTemplates,
    globalEmailTrackingUnsubscribeTemplates,
    emailTrackingUnsubscribeSiteTemplates,
    browseTemplates,
    mpsEmbedTemplates,
    mpsPopupTemplates,
    bulkRetargetingTemplates,
    templates,
    sendMyBasketTemplates,
    sendMyBasketEmailTemplates,
    smsBrowseAbandonmentTemplates,
    smsCartAbandonmentTemplates,
    rcsBrowseAbandonmentTemplates,
    rcsCartAbandonmentTemplates,
    isArchived,
    templatesQueryData,
    isArchivedData,
  ]);

  useSetDefaultSelectedTemplate(
    templates,
    unsubscribeTemplates,
    globalUnsubscribeTemplates,
    globalEmailTrackingUnsubscribeTemplates,
    emailTrackingUnsubscribeSiteTemplates,
    emailAcquisitionTemplates,
    emailAcquisitionEmailTemplates,
    sendMyBasketTemplates,
    sendMyBasketEmailTemplates,
    displayOnlyTemplates,
    browseTemplates,
    mpsEmbedTemplates,
    mpsPopupTemplates,
    smsCartAbandonmentTemplates,
    smsBrowseAbandonmentTemplates,
    rcsCartAbandonmentTemplates,
    rcsBrowseAbandonmentTemplates,
    bulkRetargetingTemplates,
    filteredTemplates,
  );

  const sortTemplates = (a: ITemplate, b: ITemplate) => {
    switch (sortingFilter) {
      case sortByOptions.nameAToZ:
        return sortByAZ(a.templateName, b.templateName);
      case sortByOptions.oldestToMostRecent:
        return sortByDateDesc(Number(a.audit.lastUpdatedAt), Number(b.audit.lastUpdatedAt));
      case sortByOptions.mostRecent:
        return sortByDateAsc(Number(a.audit.lastUpdatedAt), Number(b.audit.lastUpdatedAt));
      default:
        return sortByAZ(a.templateName, b.templateName);
    }
  };

  // Scroll to selected template when it changes (e.g., after cloning)
  const prevSelectedTemplateIdRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    if (selectedTemplate && selectedTemplate.id !== prevSelectedTemplateIdRef.current) {
      const isInFilteredTemplates = filteredTemplates.some(t => t.id === selectedTemplate.id);

      if (isInFilteredTemplates) {
        prevSelectedTemplateIdRef.current = selectedTemplate.id;

        const timeoutId = setTimeout(() => {
          const templateElement = document.querySelector(`[data-template-id="${selectedTemplate.id}"]`);
          if (templateElement) {
            templateElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);

        return () => clearTimeout(timeoutId);
      } else {
        prevSelectedTemplateIdRef.current = selectedTemplate.id;
      }
    }
  }, [selectedTemplate, filteredTemplates]);

  if (
    templatesQuery.isLoading ||
    unsubscribesQuery.isLoading ||
    globalUnsubscribesQuery.isLoading ||
    (isGlobalEmailTrackingUnsubscribeGrid && globalEmailTrackingUnsubscribesQuery.isLoading) ||
    (isSiteEmailTrackingUnsubscribeGrid && emailTrackingUnsubscribeSiteQuery.isLoading) ||
    emailAcquisitionsQuery.isLoading ||
    sendMyBasketQuery.isLoading ||
    displayOnlyQuery.isLoading ||
    mpsEmbedQuery.isLoading ||
    mpsPopupQuery.isLoading
  ) {
    return <Spinner />;
  }

  if (
    templatesQuery.isError ||
    unsubscribesQuery.isError ||
    globalUnsubscribesQuery.isError ||
    (isGlobalEmailTrackingUnsubscribeGrid && globalEmailTrackingUnsubscribesQuery.isError) ||
    (isSiteEmailTrackingUnsubscribeGrid && emailTrackingUnsubscribeSiteQuery.isError) ||
    emailAcquisitionsQuery.isError ||
    sendMyBasketQuery.isError ||
    displayOnlyQuery.isError ||
    mpsEmbedQuery.isError ||
    mpsPopupQuery.isError
  ) {
    return (
      <Splash
        imageUrl="/images/error-plug-pink.svg"
        heading={t('designs:designGallery.templateList.queries.getTemplates.error')}
        imageHeight={150}
      />
    );
  }

  if (!supportedLanguages || supportedLanguages.length < 1) {
    return (
      <Splash
        imageUrl="/images/designs/design_gallery/bluetile.png"
        heading={t('designs:designGallery.noSiteLanguagesSettingsHeading')}
        imageHeight={150}
      />
    );
  }

  if (templateType === 'globalUnsubscribe' || templateType === 'globalUnsubscribeEmailTracking') {
    return (
      <TemplatesGridWrapper>
        <Grid columnNumber={6} columnMinWidth={{ value: 150, unit: 'px' }} gridGap={10}>
          {filteredTemplates.length > 0 ? (
            filteredTemplates
              .sort((a, b) => sortTemplates(a, b))
              .map((template: ITemplate) => {
                return <TemplatePanel key={template.id} template={template} />;
              })
          ) : (
            <Splash
              imageUrl="/images/designs/design_gallery/bluetile.png"
              heading={t('designs:designGallery.noTemplatesHeading')}
              imageHeight={150}
            />
          )}
        </Grid>
      </TemplatesGridWrapper>
    );
  } else {
    const emptyHeading = isArchived
      ? t('designs:designGallery.noArchivedTemplatesHeading')
      : t('designs:designGallery.noTemplatesHeading');

    if (isArchived && filteredTemplates.length === 0) {
      return (
        <TemplatesGridWrapper>
          <Splash imageUrl="/images/designs/design_gallery/bluetile.png" heading={emptyHeading} imageHeight={150} />
        </TemplatesGridWrapper>
      );
    }

    return (
      <TemplatesGridWrapper>
        <Grid columnNumber={6} columnMinWidth={{ value: 150, unit: 'px' }} gridGap={10}>
          {!isArchived && <CreateTemplatePanel channel={channel} templateType={templateType} />}
          {filteredTemplates.length > 0 ? (
            filteredTemplates
              .sort((a, b) => sortTemplates(a, b))
              .map((template: ITemplate) => {
                return <TemplatePanel key={template.id} template={template} />;
              })
          ) : (
            <Splash imageUrl="/images/designs/design_gallery/bluetile.png" heading={emptyHeading} imageHeight={150} />
          )}
        </Grid>
      </TemplatesGridWrapper>
    );
  }
};

export default TemplatesGrid;
